import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import axios from 'axios';
import { Package, Truck, DollarSign, Recycle, Calendar, TrendingUp, ArrowUpRight, ArrowDownRight, Users, Box, ChevronDown, ShieldAlert, X } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function Dashboard() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [periodOptions, setPeriodOptions] = useState(null);
  const [periodLabel, setPeriodLabel] = useState('');

  // Stock-health passive banner — survives until user dismisses it for this session
  const [healthIssues, setHealthIssues] = useState(null);
  const [healthDismissed, setHealthDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem('stock_health_banner_dismissed') === '1'
  );
  useEffect(() => {
    if (healthDismissed) return;
    axios.get(`${API}/stores/consumable-stock/health`)
      .then((r) => {
        const s = r.data?.summary || {};
        const total = (s.orphanLots || 0) + (s.orphanInventory || 0) + (s.orphanIssues || 0) + (s.stockDrift || 0);
        if (total > 0) setHealthIssues({ total, summary: s });
      })
      .catch(() => {});
  }, [API, healthDismissed]);

  const dismissHealthBanner = () => {
    sessionStorage.setItem('stock_health_banner_dismissed', '1');
    setHealthDismissed(true);
    setHealthIssues(null);
  };
  
  // Period selection states
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMonthYear, setSelectedMonthYear] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedQuarterFY, setSelectedQuarterFY] = useState(null);
  const [selectedFY, setSelectedFY] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null); // 'month' | 'quarter' | 'year' | null
  const dropdownRef = useRef(null);
  
  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Default colors for pie charts
  const customerColors = ['#1a237e', '#2e7d32', '#e65100', '#7b1fa2', '#546e7a'];
  const productColors = ['#1565c0', '#00897b', '#ef6c00', '#8e24aa', '#78909c'];

  useEffect(() => {
    fetchPeriodOptions();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [period, selectedMonth, selectedMonthYear, selectedQuarter, selectedQuarterFY, selectedFY]);

  const fetchPeriodOptions = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/period-options`);
      setPeriodOptions(response.data);
    } catch (error) {
      console.error('Error fetching period options:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Build query params based on period
      let params = `period=${period}`;
      if (period === 'month' && selectedMonth !== null && selectedMonthYear !== null) {
        params += `&month=${selectedMonth}&year=${selectedMonthYear}`;
      } else if (period === 'quarter' && selectedQuarter !== null && selectedQuarterFY !== null) {
        params += `&quarter=${selectedQuarter}&fy=${selectedQuarterFY}`;
      } else if (period === 'year' && selectedFY !== null) {
        params += `&fy=${selectedFY}`;
      }
      
      const response = await axios.get(`${API}/dashboard/analytics?${params}`);
      const data = response.data;
      
      setPeriodLabel(data.periodLabel || '');
      
      setAnalytics({
        kpis: {
          total_production: data.kpis?.total_production || 0,
          total_dispatch: data.kpis?.total_dispatch || 0,
          total_revenue: data.kpis?.total_revenue || 0,
          material_recovery: data.kpis?.material_recovery || 0
        },
        production_dispatch: data.production_dispatch || [],
        revenue_trend: data.revenue_trend || [],
        material_recovery: data.material_recovery || [],
        sales_price: data.sales_price || [],
        customer_distribution: data.customer_distribution || [],
        product_distribution: data.product_distribution || [],
        counts: data.counts || {},
        recentActivity: data.recentActivity || {}
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Set default values on error
      setAnalytics({
        kpis: {
          total_production: 0,
          total_dispatch: 0,
          total_revenue: 0,
          material_recovery: 0
        },
        production_dispatch: [],
        revenue_trend: [],
        material_recovery: [],
        sales_price: [],
        customer_distribution: [],
        product_distribution: [],
        counts: {},
        recentActivity: {}
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-[#1a237e] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Safe number formatter with fallback
  const formatNumber = (num, divisor = 1, suffix = '', decimals = 2) => {
    const value = num || 0;
    return `${(value / divisor).toFixed(decimals)}${suffix}`;
  };

  // Format currency in Indian notation (Lakhs, Crores)
  const formatCurrency = (num) => {
    const value = num || 0;
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(2)} K`;
    }
    return `₹${value.toFixed(0)}`;
  };

  const kpiCards = [
    {
      title: 'Total Production',
      value: formatNumber(analytics?.kpis?.total_production, 1000, ' MT', 1),
      subtitle: 'Finished goods produced',
      change: '+12.5%',
      changeType: 'positive',
      icon: Package,
      gradient: 'from-[#1a237e] to-[#3949ab]',
      bgGradient: 'from-indigo-50 to-blue-50',
      borderColor: 'border-indigo-200'
    },
    {
      title: 'Total Dispatch',
      value: formatNumber(analytics?.kpis?.total_dispatch, 1, ' MT', 1),
      subtitle: 'Goods dispatched',
      change: '+8.3%',
      changeType: 'positive',
      icon: Truck,
      gradient: 'from-[#2e7d32] to-[#43a047]',
      bgGradient: 'from-green-50 to-emerald-50',
      borderColor: 'border-green-200'
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(analytics?.kpis?.total_revenue),
      subtitle: 'Revenue from dispatch',
      change: '+15.2%',
      changeType: 'positive',
      icon: DollarSign,
      gradient: 'from-[#e65100] to-[#f57c00]',
      bgGradient: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200'
    },
    {
      title: 'Material Recovery',
      value: `${(analytics?.kpis?.material_recovery || 0).toFixed(1)}%`,
      subtitle: 'Average recovery rate',
      change: '-2.1%',
      changeType: 'negative',
      icon: Recycle,
      gradient: 'from-[#7b1fa2] to-[#9c27b0]',
      bgGradient: 'from-purple-50 to-fuchsia-50',
      borderColor: 'border-purple-200'
    },
  ];

  const periods = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'year', label: 'Year' },
  ];

  const productionDispatchData = analytics?.production_dispatch?.map(item => ({
    date: item.date || item.name,  // Support both 'date' and 'name' fields
    Production: item.production,
    Dispatch: item.dispatch
  })) || [];

  // Generate revenue data from production_dispatch if not available
  const revenueData = analytics?.revenue_trend?.length > 0 
    ? analytics.revenue_trend.map(item => ({
        date: item.date || item.name,
        revenue: item.value
      }))
    : productionDispatchData.map(item => ({
        date: item.date,
        revenue: item.Dispatch * 280000  // Approximate revenue per MT
      }));

  // Generate sales price data from production_dispatch if not available
  const salesPriceData = analytics?.sales_price?.length > 0
    ? analytics.sales_price.map(item => ({
        date: item.date || item.name,
        price: item.value
      }))
    : productionDispatchData.map(item => ({
        date: item.date,
        price: item.Dispatch > 0 ? 280000 : 0  // Approximate price per MT
      }));

  // Generate material recovery data from production_dispatch if not available  
  const materialRecoveryData = analytics?.material_recovery?.length > 0
    ? analytics.material_recovery.map(item => ({
        date: item.date || item.name,
        recovery: item.value
      }))
    : productionDispatchData.map(item => ({
        date: item.date,
        recovery: item.Production > 0 ? 92 : 0  // Default recovery rate
      }));

  // Use dynamic customer and product data from backend
  const customerData = analytics?.customer_distribution?.length > 0 
    ? analytics.customer_distribution.map((item, idx) => ({
        name: item.name,
        value: item.value,
        quantity: item.quantity,
        color: item.color || customerColors[idx] || '#546e7a'
      }))
    : [{ name: 'No Data', value: 100, color: '#e0e0e0' }];

  const productData = analytics?.product_distribution?.length > 0
    ? analytics.product_distribution.map((item, idx) => ({
        name: item.name,
        value: item.value,
        quantity: item.quantity,
        color: item.color || productColors[idx] || '#78909c'
      }))
    : [{ name: 'No Data', value: 100, color: '#e0e0e0' }];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Stock Health Banner — fires when /api/stores/consumable-stock/health
          reports any data-integrity issues. Per-session dismissible. */}
      {healthIssues && (
        <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="stock-health-banner">
          <ShieldAlert size={22} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-900">
              Stock data integrity needs attention — {healthIssues.total} issue{healthIssues.total === 1 ? '' : 's'} detected
            </p>
            <p className="text-xs text-rose-700 mt-0.5 truncate">
              {healthIssues.summary.orphanLots > 0 && <>· <strong>{healthIssues.summary.orphanLots}</strong> orphan lots </>}
              {healthIssues.summary.orphanInventory > 0 && <>· <strong>{healthIssues.summary.orphanInventory}</strong> orphan inventory rows </>}
              {healthIssues.summary.orphanIssues > 0 && <>· <strong>{healthIssues.summary.orphanIssues}</strong> tainted material issues </>}
              {healthIssues.summary.stockDrift > 0 && <>· <strong>{healthIssues.summary.stockDrift}</strong> drift items </>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => navigate('/stores/consumable-stock/health')}
                    className="px-3 py-1.5 bg-rose-600 text-white text-sm rounded-lg hover:bg-rose-700 font-medium"
                    data-testid="goto-stock-health">
              Review & Fix
            </button>
            <button onClick={dismissHealthBanner}
                    className="p-1.5 text-rose-700 hover:bg-rose-100 rounded-lg"
                    title="Dismiss for this session"
                    data-testid="dismiss-health-banner">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">Comprehensive business insights and metrics</p>
        </div>

        {/* Period Filters */}
        <div className="flex items-center gap-3">
          <div className="flex items-center space-x-1 bg-white rounded-xl shadow-sm p-1.5 border border-gray-200">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => {
                  setPeriod(p.value);
                  // Reset sub-selections when switching tabs
                  if (p.value === 'week') {
                    setSelectedMonth(null); setSelectedMonthYear(null);
                    setSelectedQuarter(null); setSelectedQuarterFY(null);
                    setSelectedFY(null);
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  period === p.value
                    ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          
          {/* Period-specific dropdowns (custom to avoid select hydration) */}
          <div ref={dropdownRef}>
          {period === 'month' && periodOptions?.months && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(showDropdown === 'month' ? null : 'month')}
                className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 shadow-sm flex items-center gap-2 hover:bg-gray-50 min-w-[160px]"
                data-testid="month-selector"
              >
                <Calendar size={14} />
                {selectedMonth !== null 
                  ? periodOptions.months.find(m => m.month === selectedMonth && m.year === selectedMonthYear)?.label || 'Select'
                  : periodOptions.months[0]?.label || 'Select'}
                <ChevronDown size={14} className="ml-auto" />
              </button>
              {showDropdown === 'month' && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-56 max-h-72 overflow-y-auto">
                  {periodOptions.months.map((m) => (
                    <button
                      key={`${m.month}-${m.year}`}
                      onClick={() => { setSelectedMonth(m.month); setSelectedMonthYear(m.year); setShowDropdown(null); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 transition-colors ${
                        selectedMonth === m.month && selectedMonthYear === m.year ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {period === 'quarter' && periodOptions?.quarters && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(showDropdown === 'quarter' ? null : 'quarter')}
                className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 shadow-sm flex items-center gap-2 hover:bg-gray-50 min-w-[220px]"
                data-testid="quarter-selector"
              >
                <Calendar size={14} />
                {selectedQuarter !== null 
                  ? periodOptions.quarters.find(q => q.quarter === selectedQuarter && q.fy === selectedQuarterFY)?.label || 'Select'
                  : periodOptions.quarters[0]?.label || 'Select'}
                <ChevronDown size={14} className="ml-auto" />
              </button>
              {showDropdown === 'quarter' && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-72 max-h-72 overflow-y-auto">
                  {periodOptions.quarters.map((q) => (
                    <button
                      key={`${q.quarter}-${q.fy}`}
                      onClick={() => { setSelectedQuarter(q.quarter); setSelectedQuarterFY(q.fy); setShowDropdown(null); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 transition-colors ${
                        selectedQuarter === q.quarter && selectedQuarterFY === q.fy ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {period === 'year' && periodOptions?.years && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(showDropdown === 'year' ? null : 'year')}
                className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 shadow-sm flex items-center gap-2 hover:bg-gray-50 min-w-[140px]"
                data-testid="year-selector"
              >
                <Calendar size={14} />
                {selectedFY !== null 
                  ? periodOptions.years.find(y => y.fy === selectedFY)?.label || 'Select'
                  : periodOptions.years[0]?.label || 'Select'}
                <ChevronDown size={14} className="ml-auto" />
              </button>
              {showDropdown === 'year' && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-48 max-h-72 overflow-y-auto">
                  {periodOptions.years.map((y) => (
                    <button
                      key={y.fy}
                      onClick={() => { setSelectedFY(y.fy); setShowDropdown(null); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 transition-colors ${
                        selectedFY === y.fy ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {y.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      
      {/* Period Label */}
      {periodLabel && (
        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="period-label">
          <Calendar size={14} />
          <span>{periodLabel}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {kpiCards.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <div
              key={index}
              className={`bg-gradient-to-br ${kpi.bgGradient} rounded-2xl p-5 border ${kpi.borderColor} shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1`}
              data-testid={`kpi-card-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                  <h3 className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</h3>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center text-xs font-semibold ${
                      kpi.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {kpi.changeType === 'positive' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                      {kpi.change}
                    </span>
                    <span className="text-xs text-gray-500">{kpi.subtitle}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production vs Dispatch */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Production vs Dispatch</h3>
              <p className="text-sm text-gray-500">Comparison of volumes</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-[#1a237e]"></span>
              <span className="text-xs text-gray-500">Production</span>
              <span className="w-3 h-3 rounded-full bg-[#2e7d32] ml-2"></span>
              <span className="text-xs text-gray-500">Dispatch</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={productionDispatchData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                formatter={(value, name) => [`${value} MT`, name]}
              />
              <Bar dataKey="Dispatch" fill="#2e7d32" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Production" fill="#1a237e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900">Revenue Trend</h3>
            <p className="text-sm text-gray-500">Revenue performance over time</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => value >= 100000 ? `${(value/100000).toFixed(0)}L` : `${(value/1000).toFixed(0)}K`} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                formatter={(value) => [`₹${value >= 100000 ? (value/100000).toFixed(2) + 'L' : (value/1000).toFixed(2) + 'K'}`, 'Revenue']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#e65100" fill="url(#revenueGradient)" strokeWidth={2} />
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#e65100" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#e65100" stopOpacity={0}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sales Price Trend */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900">Sales Price Trend</h3>
            <p className="text-sm text-gray-500">Average sales price per MT</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={salesPriceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                formatter={(value) => [`₹${value >= 100000 ? (value/100000).toFixed(2) + 'L' : value.toLocaleString()}`, 'Price/MT']}
              />
              <Line type="monotone" dataKey="price" stroke="#7b1fa2" strokeWidth={2} dot={{ fill: '#7b1fa2', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Material Recovery Rate */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900">Material Recovery Rate</h3>
            <p className="text-sm text-gray-500">Percentage of material recovered</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={materialRecoveryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                formatter={(value) => `${value}%`}
              />
              <Area type="monotone" dataKey="recovery" stroke="#2e7d32" fill="url(#recoveryGradient)" strokeWidth={2} />
              <defs>
                <linearGradient id="recoveryGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0}/>
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Customer-wise Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="mb-6">
            <div className="flex items-center space-x-2">
              <Users className="text-[#1a237e]" size={20} />
              <h3 className="text-lg font-bold text-gray-900">Customer-wise Distribution</h3>
            </div>
            <p className="text-sm text-gray-500">Sales distribution by customer</p>
          </div>
          <div className="flex items-center">
            <ResponsiveContainer width="60%" height={250}>
              <PieChart>
                <Pie
                  data={customerData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {customerData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `${value}%`}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="w-[40%] space-y-2">
              {customerData.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                    <span className="text-gray-600 truncate max-w-[120px]">{item.name}</span>
                  </div>
                  <span className="font-semibold text-gray-900">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product-wise Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="mb-6">
            <div className="flex items-center space-x-2">
              <Box className="text-[#e65100]" size={20} />
              <h3 className="text-lg font-bold text-gray-900">Product-wise Distribution</h3>
            </div>
            <p className="text-sm text-gray-500">Production distribution by product type</p>
          </div>
          <div className="flex items-center">
            <ResponsiveContainer width="60%" height={250}>
              <PieChart>
                <Pie
                  data={productData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {productData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `${value}%`}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="w-[40%] space-y-2">
              {productData.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                    <span className="text-gray-600 truncate max-w-[120px]">{item.name}</span>
                  </div>
                  <span className="font-semibold text-gray-900">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
